<template>
  <div class="checkout-page cod-shipping-fee-checkout">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('orderPage.checkoutTitle')" :border="false" left-arrow @click-left="onClickLeft" />

    <div v-if="loading || !initialLoadDone" class="checkout-page__state">{{ $t('loading') }}</div>
    <div v-else-if="loadError" class="checkout-page__state checkout-page__state--error">
      <div>{{ $t('systemError') }}</div>
      <button type="button" @click="loadOrderDetail">{{ $t('retry') }}</button>
    </div>
    <template v-else-if="orderDetail">
      <div class="checkout-page__safe store-checkout__safe">
        <div class="safe-strip-icon" aria-hidden="true">
          <span class="safe-strip-icon__shield"></span>
        </div>
        <div class="store-checkout__safe-copy"><strong>Transaction secure &amp; encrypted</strong><span>Your payment is protected; pay when the device arrives.</span></div>
      </div>

      <section class="checkout-page__amount-card">
        <div>
          <div class="amount-card__label">Total Payment</div>
          <div class="amount-card__value">{{ checkoutPriceUnit }} {{ codShippingFeeAmount | numberFormat }}</div>
        </div>
        <div class="receipt-illustration">
          <div class="receipt-illustration__paper">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div class="receipt-illustration__coin">Rp</div>
        </div>
        <div class="amount-card__divider"></div>
        <div class="section-title amount-card__detail-toggle" :class="{ 'amount-card__detail-toggle--expanded': detailExpanded }" @click="detailExpanded = !detailExpanded">
          <span>Order Details</span>
          <van-icon name="arrow-down" />
        </div>
      <transition name="details-expand">
        <div v-if="detailExpanded" class="details-body amount-card__details-body">
          <div class="checkout-product">
            <div class="checkout-product__image" :class="{ 'checkout-product__image--empty': !orderDetail.goodsPic }">
              <img v-if="orderDetail.goodsPic" :src="orderDetail.goodsPic" :alt="orderDetail.goodsName" />
            </div>
            <div class="checkout-product__info">
              <div class="checkout-product__name">{{ orderDetail.goodsName }}</div>
              <div class="checkout-product__sku">{{ orderDetail.skuInfo }}</div>
            </div>
          </div>
          <div class="detail-row"><span>Order No.</span><span>{{ orderDetail.orderId }}</span></div>
          <div class="detail-row"><span>{{ $t('orderPage.securityDeposit') }}</span><span>{{ checkoutPriceUnit }}{{ orderDetail.depositAmount | numberFormat }}</span></div>
          <div class="detail-row"><span>{{ $t('orderPage.firstRentalAmount') }}</span><span>{{ checkoutPriceUnit }}{{ storeFirstRentalAmount | numberFormat }}</span></div>
        </div>
      </transition>
      </section>

      <section class="checkout-page__payment-section">
        <div class="section-heading">{{ $t('orderPage.choosePaymentMethod') }}</div>
        <PayMethodSelector
          :value.sync="selectedPayMethod"
          :cod-enabled="false"
          :online-methods="onlinePayMethods"
          :disabled-methods="disabledOnlinePayMethods"
        />
      </section>
    </template>

    <div v-if="orderDetail" class="checkout-page__footer">
      <div class="footer-total">
        <div class="footer-total__label">{{ $t('orderPage.totalPembayaran') }}</div>
        <div class="footer-total__value">{{ checkoutPriceUnit }} {{ codShippingFeeAmount | numberFormat }}</div>
      </div>
      <div class="footer-detail" @click="detailExpanded = !detailExpanded">
        Order Details <van-icon :name="detailExpanded ? 'arrow-up' : 'arrow-down'" />
      </div>
      <button type="button" class="checkout-btn" :class="{ 'btn-disabled': submitting || !canPayCodShippingFee }" :disabled="submitting || !canPayCodShippingFee" @click="submitPayment">
        <van-icon name="lock" />
        <span>{{ submitting ? $t('loading') : $t('orderPage.checkoutOnline') }}</span>
      </button>
    </div>

    <PaymentPopup ref="paymentPopup" @close="onPaymentPopupClose" />
    <van-popup v-model="showPayUrlPopup" position="bottom">
      <div class="pay-url-popup">
        <div class="pay-url-popup__close" @click="showPayUrlPopup = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="pay-url-popup__title">{{ $t('orderPage.payUrlTitle') }}</div>
        <div class="pay-url-popup__desc">{{ $t('orderPage.payUrlDesc') }}</div>
        <div class="pay-url-popup__footer">
          <button type="button" class="pay-url-popup__primary" @click="goToPayUrl">{{ $t('orderPage.goToPay') }}</button>
          <button
            type="button"
            class="pay-url-popup__completed"
            :disabled="checkingPaymentStatus"
            @click="checkCodShippingFeePaymentStatus"
          >
            {{ $t('orderPage.paymentCompletedButton') }}
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Icon, Popup } from 'vant';
import PayMethodSelector from '@/components/PayMethodSelector.vue';
import PaymentPopup from '@/components/PaymentPopup/index.vue';
import { getAppSystemConfig } from '@/api/common';
import { queryOrderDetail } from '@/api/order';
import { createStorePayment, queryLatestPendingPayment } from '@/api/payment';
import { ORDER_STATUS, PAY_METHOD } from '@/common/constants';
import { mapState } from 'vuex';
import { DEFAULT_REG_CHANNEL } from '@/config';

const RISK_AUDIT_STATUS = {
  PASSED: 'PASSED'
};

const COD_SHIPPING_FEE_PAY_STATUS = {
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  SUCCESS: 'SUCCESS'
};
const COD_SHIPPING_FEE_THIRD_PARTY_RETURN_QUERY = 'fromThirdParty';
const ONLINE_PAY_CHANNELS = {
  ayolinx: { titleKey: 'orderPage.onlinePayQrisTitle', descKey: 'orderPage.onlinePayQrisDesc', iconType: 'qris', tags: [] },
  ayolinx_paylink: { titleKey: 'orderPage.onlinePayAggregatedTitle', descKey: 'orderPage.onlinePayAggregatedDesc', iconType: 'wallet', tags: ['VA', 'E-wallet', 'QRIS'] },
  paycools: { titleKey: 'orderPage.onlinePayAggregatedTitle', descKey: 'orderPage.onlinePayAggregatedDesc', iconType: 'wallet', tags: ['VA', 'E-wallet', 'QRIS'] }
};

export default {
  name: 'StorePaymentCheckout',
  components: {
    PayMethodSelector,
    PaymentPopup,
    'van-icon': Icon,
    [Popup.name]: Popup
  },
  data() {
    return {
      PAY_METHOD,
      selectedPayMethod: PAY_METHOD.ONLINE,
      onlinePayMethods: [],
      systemConfigLoaded: false,
      orderId: '',
      orderDetail: null,
      loading: true,
      initialLoadDone: false,
      loadError: false,
      submitting: false,
      detailExpanded: false,
      checkingPaymentStatus: false,
      showPayUrlPopup: false,
      activePayUrl: '',
      pendingPayment: null,
      resumeFromThirdParty: false,
      pollingTimer: null,
      pollingAttempts: 0,
      maxPollingAttempts: 60,
      skipNextPaymentPopupCloseRefresh: false,
      skipNextPayUrlPopupCloseRefresh: false
    };
  },
  computed: {
    ...mapState({
      channel: state => state.user.channel || DEFAULT_REG_CHANNEL
    }),
    priceUnit() {
      return this.$t('priceUnit');
    },
    checkoutPriceUnit() {
      return this.priceUnit;
    },
    codShippingFeeAmount() {
      return Number(this.orderDetail?.firstPayAmount || this.orderDetail?.totalAmount || 0);
    },
    storeFirstRentalAmount() {
      return Number(this.orderDetail?.planPrice || 0) + Number(this.orderDetail?.riskAmount || 0);
    },
    normalizedOrderStatus() {
      return this.normalizeStatus(this.orderDetail?.status);
    },
    riskAuditStatus() {
      return this.normalizeStatus(this.orderDetail?.riskAuditStatus);
    },
    codShippingFeePayStatus() {
      return this.normalizeStatus(this.orderDetail?.codShippingFeePayStatus);
    },
    isCodOrder() {
      return this.normalizeStatus(this.orderDetail?.payMethod) === PAY_METHOD.STORE;
    },
    isCodShippingFeePaid() {
      return this.normalizeStatus(this.orderDetail?.payStatus) === COD_SHIPPING_FEE_PAY_STATUS.SUCCESS;
    },
    pendingPaymentChannel() {
      return String(this.pendingPayment?.payChannel || '').trim();
    },
    disabledOnlinePayMethods() {
      if (!this.pendingPaymentChannel) return [];
      return this.onlinePayMethods
        .filter(item => item.key !== this.pendingPaymentChannel)
        .map(item => item.key);
    },
    effectivePayChannel() {
      if (this.pendingPaymentChannel) return this.pendingPaymentChannel;
      return this.isValidOnlinePayMethod(this.selectedPayMethod) ? this.selectedPayMethod : '';
    },
    canPayCodShippingFee() {
      return this.isCodOrder &&
        this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED &&
        !this.isCodShippingFeePaid;
    }
  },
  watch: {
    showPayUrlPopup(show) {
      if (!show) {
        this.activePayUrl = '';
        if (this.skipNextPayUrlPopupCloseRefresh) {
          this.skipNextPayUrlPopupCloseRefresh = false;
          return;
        }
        this.refreshPaymentStateAfterPopupClose();
      }
    }
  },
  async mounted() {
    this.orderId = this.$route.query.orderId || '';
    if (!this.orderId) {
      this.$router.replace({ name: 'OrderList' });
      return;
    }
    this.scrollToTop();
    this.resumeFromThirdParty = String(this.$route.query[COD_SHIPPING_FEE_THIRD_PARTY_RETURN_QUERY] || '') === '1';
    await Promise.all([
      this.loadOrderDetail(),
      this.loadSystemFeatures(),
      this.refreshLatestPendingPayment()
    ]);
    this.syncSelectedPayMethod();
    if (this.resumeFromThirdParty) {
      this.resumePaymentFromThirdParty();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  beforeRouteLeave(to, from, next) {
    this.stopPolling();
    next();
  },
  beforeRouteUpdate(to, from, next) {
    this.scrollToTop();
    next();
  },
  methods: {
    normalizeStatus(value = '') {
      return String(value == null ? '' : value).trim().toUpperCase();
    },
    onClickLeft() {
      this.$router.replace({
        name: 'OrderDetail',
        query: { orderId: this.orderId }
      });
    },
    async loadOrderDetail({ silent = false } = {}) {
      if (!silent) {
        this.loading = true;
      }
      this.loadError = false;
      try {
        const res = await queryOrderDetail({ orderId: this.orderId });
        if (res?.code === 'Success') {
        this.orderDetail = res.data || null;
        if (this.isCodShippingFeePaid) {
          this.stopPolling();
        }
        } else if (!this.orderDetail) {
          this.loadError = true;
        }
        return res;
      } catch (e) {
        console.error('Load COD Service Fee checkout order failed:', e);
        if (!this.orderDetail) {
          this.loadError = true;
        }
        return null;
      } finally {
        this.loading = false;
        this.initialLoadDone = true;
      }
    },
    getDefaultOnlinePayMethod() {
      return this.onlinePayMethods[0]?.key || PAY_METHOD.ONLINE;
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    getDefaultPayMethod() {
      return this.onlinePayMethods.length ? this.onlinePayMethods[0].key : PAY_METHOD.ONLINE;
    },
    isValidOnlinePayMethod(value) {
      return !!value && this.onlinePayMethods.some(item => item.key === value);
    },
    restoreSelectedPayMethod() {
      const candidate = this.$route.query.payMethod || '';
      const payMethod = candidate.includes('?') ? candidate.split('?')[0] : candidate;
      if (this.isValidOnlinePayMethod(payMethod)) {
        this.selectedPayMethod = payMethod;
        return;
      }
      this.selectedPayMethod = this.getDefaultPayMethod();
    },
    syncSelectedPayMethod() {
      if (this.pendingPaymentChannel) {
        this.selectedPayMethod = this.pendingPaymentChannel;
        return;
      }
      if (this.isValidOnlinePayMethod(this.selectedPayMethod)) {
        return;
      }
      this.restoreSelectedPayMethod();
    },
    async loadSystemFeatures() {
      if (this.systemConfigLoaded) return;
      try {
        const res = await getAppSystemConfig({ platform: 'h5', channel: this.channel || DEFAULT_REG_CHANNEL });
        const payments = res?.data?.features?.payments || [];
        const methods = (Array.isArray(payments) ? payments : [])
          .map(key => {
            const config = ONLINE_PAY_CHANNELS[key];
            if (!config) return null;
            return {
              key,
              title: this.$t(config.titleKey),
              desc: this.$t(config.descKey),
              iconType: config.iconType,
              tags: config.tags
            };
          })
          .filter(Boolean);
        this.onlinePayMethods = methods;
      } catch (e) {
        console.warn('Failed to load COD shipping fee payment methods:', e);
      } finally {
        this.systemConfigLoaded = true;
      }
    },
    async refreshLatestPendingPayment() {
      try {
        const res = await queryLatestPendingPayment({
          orderId: this.orderId,
          payScene: 'PAY'
        });
        this.pendingPayment = res?.code === 'Success' ? (res.data || null) : null;
      } catch (e) {
        this.pendingPayment = null;
      }
    },
    async refreshPaymentStateAfterPopupClose() {
      await Promise.all([
        this.loadOrderDetail({ silent: true }),
        this.refreshLatestPendingPayment()
      ]);
      this.syncSelectedPayMethod();
    },
    buildReturnUrl() {
      if (typeof window === 'undefined' || !window.location) return '';
      const url = new URL(window.location.href);
      const payMethod = this.pendingPaymentChannel ||
        (this.isValidOnlinePayMethod(this.selectedPayMethod) ? this.selectedPayMethod : '');
      if (payMethod) {
        url.searchParams.set('payMethod', payMethod);
      } else {
        url.searchParams.delete('payMethod');
      }
      url.searchParams.set(COD_SHIPPING_FEE_THIRD_PARTY_RETURN_QUERY, '1');
      return url.toString();
    },
    clearThirdPartyReturnQuery() {
      const query = { ...this.$route.query };
      delete query[COD_SHIPPING_FEE_THIRD_PARTY_RETURN_QUERY];
      this.$router.replace({
        path: this.$route.path,
        query
      });
    },
    resumePaymentFromThirdParty() {
      if (!this.resumeFromThirdParty) return;
      this.resumeFromThirdParty = false;
      this.clearThirdPartyReturnQuery();
      this.$nextTick(() => {
        this.submitPayment();
      });
    },
    async submitPayment() {
      if (this.submitting) return;
      if (!this.canPayCodShippingFee) {
        await this.loadOrderDetail({ silent: true });
        if (this.isCodShippingFeePaid) {
          this.$toast({ message: this.$t('payment.success'), duration: 2000 });
          this.onClickLeft();
          return;
        }
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
        return;
      }
      this.submitting = true;
      try {
        await this.refreshLatestPendingPayment();
        this.syncSelectedPayMethod();
        const res = await createStorePayment({
          orderId: this.orderId,
          returnUrl: this.buildReturnUrl(),
          payChannel: this.effectivePayChannel
        });
        if (res?.code !== 'Success') {
          this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
          return;
        }
        const payInfo = res.data || {};
        this.orderDetail = {
          ...this.orderDetail,
          codShippingFee: payInfo.amount || this.codShippingFeeAmount,
          codShippingFeePayStatus: payInfo.payStatus || this.orderDetail?.codShippingFeePayStatus
        };
        if (this.normalizeStatus(payInfo.payStatus) === COD_SHIPPING_FEE_PAY_STATUS.SUCCESS) {
          this.skipNextPayUrlPopupCloseRefresh = true;
          this.skipNextPaymentPopupCloseRefresh = true;
          this.$toast({ message: this.$t('payment.success'), duration: 2000 });
          this.showPayUrlPopup = false;
          if (this.$refs.paymentPopup) {
            this.$refs.paymentPopup.close();
          }
          this.onClickLeft();
          return;
        }
        if (String(payInfo.payContent || '').trim()) {
          this.$refs.paymentPopup.open({
            payContent: payInfo.payContent,
            amount: payInfo.amount || this.codShippingFeeAmount
          });
          this.startPolling();
          return;
        }
        if (String(payInfo.payUrl || '').trim()) {
          this.activePayUrl = payInfo.payUrl;
          this.showPayUrlPopup = true;
          return;
        }
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } catch (e) {
        console.error('Create COD Service Fee payment failed:', e);
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } finally {
        this.submitting = false;
      }
    },
    async onPaymentPopupClose() {
      this.stopPolling();
      if (this.skipNextPaymentPopupCloseRefresh) {
        this.skipNextPaymentPopupCloseRefresh = false;
        return;
      }
      await this.refreshPaymentStateAfterPopupClose();
    },
    startPolling() {
      this.stopPolling();
      this.pollingAttempts = 0;
      this.pollingTimer = setInterval(() => {
        this.pollingAttempts += 1;
        if (this.pollingAttempts > this.maxPollingAttempts) {
          this.stopPolling();
          return;
        }
        this.checkCodShippingFeePaymentStatus({ silentPending: true });
      }, 30000);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    goToPayUrl() {
      if (!this.activePayUrl) return;
      window.open(this.activePayUrl, '_blank', 'noopener,noreferrer');
    },
    async checkCodShippingFeePaymentStatus({ silentPending = false } = {}) {
      if (this.checkingPaymentStatus) return;
      this.checkingPaymentStatus = true;
      try {
        const res = await this.loadOrderDetail({ silent: true });
        if (res?.code !== 'Success') {
          if (!silentPending) {
            this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
          }
          return;
        }
        if (this.isCodShippingFeePaid) {
          this.stopPolling();
          this.skipNextPayUrlPopupCloseRefresh = true;
          this.skipNextPaymentPopupCloseRefresh = true;
          this.showPayUrlPopup = false;
          if (this.$refs.paymentPopup) {
            this.$refs.paymentPopup.close();
          }
          this.$toast({ message: this.$t('payment.success'), duration: 2000 });
          this.onClickLeft();
          return;
        }
        if (!silentPending) {
          this.$toast({ message: this.$t('orderPage.paymentNotCompleted'), duration: 3000 });
        }
      } finally {
        this.checkingPaymentStatus = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.checkout-page {
  min-height: 100vh;
  padding: 118px 30px 270px;
  box-sizing: border-box;
  background: #FFFFFF;

  ::v-deep .van-nav-bar {
    height: 108px;
    line-height: 108px;
    background: #FFFFFF;
  }

  ::v-deep .van-nav-bar__title {
    color: #0B0B12;
    font-size: 36px;
    font-weight: 700;
  }

  ::v-deep .van-nav-bar .van-icon {
    color: #0B0B12;
    font-size: 42px;
  }

  &__state {
    padding-top: 180px;
    color: #6B7285;
    font-size: 28px;
    line-height: 40px;
    text-align: center;

    button {
      min-width: 160px;
      height: 64px;
      margin-top: 24px;
      border: 0;
      border-radius: 12px;
      color: #FFFFFF;
      background: #2563EB;
      font-size: 26px;
    }
  }

  &__safe {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 64px;
    padding: 0 32px;
    border-radius: 22px;
    background: linear-gradient(100deg, #F4FCF8 0%, #EFF8F4 100%);
    color: #2FA76F;
    font-size: 28px;
    line-height: 36px;
    box-sizing: border-box;
  }

  &__amount-card {
    position: relative;
    min-height: 238px;
    margin-top: 24px;
    padding: 30px 38px 0;
    border: 1px solid #EEF0F5;
    border-radius: 24px;
    background: #FFFFFF;
    box-shadow: 0 12px 30px rgba(24, 37, 72, 0.10);
    box-sizing: border-box;
    overflow: hidden;
  }

  &__payment-section {
    margin-top: 28px;
  }

  &__footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px 20px;
    padding: 22px 34px 36px;
    background: #FFFFFF;
    box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
  }
}

.safe-strip-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 32px;

  &__shield {
    position: relative;
    width: 25px;
    height: 29px;
    border-radius: 13px 13px 10px 10px;
    background: #28A96B;
    clip-path: polygon(50% 0, 92% 14%, 84% 72%, 50% 100%, 16% 72%, 8% 14%);

    &::after {
      position: absolute;
      content: '';
      left: 7px;
      top: 10px;
      width: 10px;
      height: 6px;
      border-left: 3px solid #FFFFFF;
      border-bottom: 3px solid #FFFFFF;
      transform: rotate(-45deg);
    }
  }
}

.amount-card {
  &__label {
    color: #6B7285;
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
  }

  &__value {
    max-width: calc(100% - 190px);
    margin-top: 16px;
    color: #0B45FF;
    font-size: 54px;
    line-height: 62px;
    font-weight: 400;
    white-space: nowrap;
  }

  &__divider {
    margin-top: 28px;
    border-top: 1px solid #E3E6ED;
  }

  &__detail-toggle {
    height: 78px;

    .van-icon {
      transition: transform 0.26s ease;
    }

    &--expanded .van-icon {
      transform: rotate(180deg);
    }
  }

  &__details-body {
    margin: 0 0 30px;
    padding-top: 4px;
    transform-origin: top center;
  }

  &__notice {
    margin-top: 16px;
    padding-bottom: 28px;
    color: #6B7285;
    font-size: 23px;
    line-height: 34px;
  }
}

.store-checkout__safe {
  height: auto;
  min-height: 112px;
  align-items: center;
  justify-content: flex-start;
  padding: 22px 28px;
}

.store-checkout__safe-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  color: #2FA76F;
  font-size: 24px;
  line-height: 32px;
}

.store-checkout__safe-copy strong { font-weight: 700; }
.store-checkout__safe-copy span { margin-top: 4px; color: #2FA76F; }

.section-title,
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
  color: #1D2437;

  .van-icon {
    font-size: 34px;
    color: #0B1635;
  }
}

.section-heading {
  margin-bottom: 20px;
  color: #70758C;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 26px;
  line-height: 36px;
  color: #6B7285;

  & + & {
    margin-top: 16px;
  }

  span:last-child {
    color: #020B3A;
    text-align: right;
    font-weight: 400;
  }
}

.checkout-product {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: 24px;

  &__image {
    flex: 0 0 auto;
    width: 76px;
    height: 76px;
    border-radius: 12px;
    background: #F4F6FB;
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--empty {
      border: 1px solid #E1E6F0;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    margin-left: 18px;
  }

  &__name {
    overflow: hidden;
    color: #020B3A;
    font-size: 26px;
    line-height: 36px;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sku {
    display: -webkit-box;
    margin-top: 8px;
    overflow: hidden;
    color: #6B7285;
    font-size: 24px;
    line-height: 32px;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

.receipt-illustration {
  position: absolute;
  right: 42px;
  top: 30px;
  width: 170px;
  height: 150px;

  &__paper {
    position: absolute;
    right: 36px;
    top: 0;
    width: 104px;
    height: 126px;
    border: 8px solid rgba(37, 101, 242, 0.60);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(243, 248, 255, 0.90), rgba(255, 255, 255, 0.85));

    &::before,
    &::after {
      position: absolute;
      content: '';
      top: -8px;
      width: 18px;
      height: 18px;
      border-right: 6px solid rgba(37, 101, 242, 0.60);
      border-bottom: 6px solid rgba(37, 101, 242, 0.60);
      border-left: 6px solid rgba(37, 101, 242, 0.60);
      border-radius: 0 0 10px 10px;
      background: #FFFFFF;
    }

    &::before { left: 28px; }
    &::after { right: 20px; }

    span {
      display: block;
      width: 46px;
      height: 8px;
      margin: 28px auto 0;
      border-radius: 8px;
      background: rgba(37, 101, 242, 0.48);

      & + span {
        margin-top: 14px;
      }
    }
  }

  &__coin {
    position: absolute;
    right: 0;
    top: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    border-radius: 50%;
    background: linear-gradient(135deg, #5AA2FF, #1259F3);
    color: #FFFFFF;
    font-size: 30px;
    font-weight: 700;
    box-shadow: 0 10px 20px rgba(18, 89, 243, 0.28);
  }
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  color: #70758C;
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
}

.details-expand-enter-active,
.details-expand-leave-active {
  max-height: 720px;
  opacity: 1;
  overflow: hidden;
  transform: translateY(0);
  transition: max-height 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease, transform 0.32s ease, padding 0.32s ease, margin 0.32s ease;
  will-change: max-height, opacity, transform;
}

.details-expand-enter,
.details-expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
  padding-top: 0;
  transform: translateY(-8px);
}

.footer-total {
  &__label {
    color: #5E6478;
    font-size: 26px;
    line-height: 34px;
  }

  &__value {
    margin-top: 4px;
    color: #0B45FF;
    font-size: 38px;
    line-height: 44px;
    font-weight: 400;
  }
}

.footer-detail {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 34px;
  color: #1767F3;
  font-size: 28px;
  line-height: 34px;
  font-weight: 600;
}

.checkout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  width: 100%;
  height: 88px;
  border: 0;
  border-radius: 14px;
  background: #1259F3;
  color: #FFFFFF;
  font-size: 32px;
  line-height: 40px;
  font-weight: 600;

  .van-icon {
    font-size: 34px;
  }

  &:disabled,
  &.btn-disabled {
    opacity: .55;
  }
}

.pay-url-popup {
  position: relative;
  padding: 48px 40px 44px;
  box-sizing: border-box;
  text-align: center;

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 36px;
    height: 36px;

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__title {
    color: #111827;
    font-size: 34px;
    line-height: 48px;
    font-weight: 700;
  }

  &__desc {
    margin-top: 20px;
    color: #6B7285;
    font-size: 26px;
    line-height: 38px;
  }

  &__footer {
    margin-top: 40px;
  }

  &__primary,
  &__completed {
    width: 100%;
    height: 80px;
    border-radius: 12px;
    font-size: 28px;
    line-height: 38px;
  }

  &__primary {
    border: 0;
    color: #FFFFFF;
    background: #2563EB;
  }

  &__completed {
    margin-top: 20px;
    border: 2px solid #2563EB;
    color: #2563EB;
    background: #FFFFFF;

    &:disabled {
      opacity: .5;
    }
  }
}
</style>
